import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { BusinessPatchDto } from "../../../../../../@types/dtos/business/BusinessPatchDto";
import { BusinessPostDto } from "../../../../../../@types/dtos/business/BusinessPostDto";
import { BusinessDetailsForm } from "../../../../../../components/form/business/details";
import { useGetBusiness } from "../../../../../../hooks/api/business/useGetBusiness";
import { useGetBusinessList } from "../../../../../../hooks/api/business/useGetBusinessList";
import { usePatchBusiness } from "../../../../../../hooks/api/business/usePatchBusiness";
import { usePostBusiness } from "../../../../../../hooks/api/business/usePostBusiness";
import { Feedback } from "../../../../../../@types/enums/general/Feedback";
import WsSnackbar, { WsSnackbarTypes } from "../../../../../../components/ws-snackbar";
import { useTranslation } from "@onefront/react-sdk";
import { getBodyDiff } from "../../../../../../utils/functions/getBodyDiff";
import { usePatchAdditionalBusinessDetails } from "../../../../../../hooks/api/business/usePatchAdditionalBusinessDetails";

export const BusinessDetails = ({ workspaceData, setBusiness }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const { workspaceId, businessId } = useParams();
    const [bodyRequest, setBodyRequest] = useState<BusinessPostDto>({
        description: "",
        atecoCode: "",
        atecoCodeDescription: "",
        type: "SECONDARY",
        selfEmployed: false
    });
    const [feedback, setFeedback] = useState<any>(null);
    const [primaryBusiness, setPrimaryBusiness] = useState<any>(null);
    const [isEditing, setIsEditing] = useState(businessId === "create");
    const [requestType, setRequestType] = useState<any>("MAIN");

    const { data: businessRes, loading, fetch: getBusinessList } = useGetBusinessList(workspaceId);

    const { data: business, loading: loadingGet, fetch: getBusiness } = useGetBusiness(workspaceId, businessId);

    const { loading: loadingPost, fetch: postBusiness } = usePostBusiness(workspaceId);

    const { loading: loadingPatch, fetch: patchBusiness } = usePatchBusiness(workspaceId, businessId);

    const { loading: loadingPatchAccounting, fetch: patchBusinessAccounting } = usePatchAdditionalBusinessDetails(
        workspaceId,
        businessId,
        "accounting-configuration",
        business?.accountingConfiguration?.id
    );

    const handleChange = useCallback((val, key) => {
        setBodyRequest(prev => ({
            ...prev,
            [key]: val
        }));
    }, []);

    const handleDataChange = useCallback(e => {
        const { value, name } = e.target;
        if (name === "selfEmployed") {
            setBodyRequest(prev => ({
                ...prev,
                [name]: value === "true"
            }));
        } else {
            setBodyRequest(prev => ({
                ...prev,
                [name]: value
            }));
        }
    }, []);

    const handleNavigate = useCallback(
        businessId => {
            const { pathname } = location;
            const newUrl = pathname.replace("create", businessId).replace(/\/details$/, "/accounting");
            navigate(newUrl);
        },
        [location, navigate]
    );

    useEffect(() => {
        if (!businessId && !loading && businessRes?.business?.length > 0) {
            setPrimaryBusiness(businessRes?.business?.find(business => business.type === "MAIN"));
        } else if (!loading && businessRes?.business?.length === 0) {
            setPrimaryBusiness(false);
            setBodyRequest(prev => ({
                ...prev,
                type: "MAIN"
            }));
        }
    }, [businessRes, loading]);

    useEffect(() => {
        if (primaryBusiness) {
            setBodyRequest(prev => ({
                ...prev,
                type: "SECONDARY"
            }));
        }
    }, [primaryBusiness]);

    useEffect(() => {
        if (businessId !== "create" && !business && !loadingGet) {
            getBusiness?.().then(res => {
                if (res.data) {
                    const { type, atecoCode, atecoCodeDescription, description, startDate, selfEmployed, healthCard } =
                        res.data;
                    setBusiness(res?.data);
                    setBodyRequest(prev => ({
                        ...prev,
                        type,
                        atecoCode,
                        atecoCodeDescription,
                        description,
                        startDate,
                        selfEmployed,
                        healthCard
                    }));
                    setIsEditing(true);
                }
            });
        }
    }, [businessId, getBusiness, business, loadingGet, setBusiness]);

    return (
        <>
            {feedback && (
                <WsSnackbar
                    position={"absolute"}
                    sx={{
                        right: "0 !important",
                        top: "-4.5rem !important",
                        width: 700
                    }}
                    type={feedback === Feedback.ERROR ? WsSnackbarTypes.error : WsSnackbarTypes.success}
                    title={t(`business.details.title`)}
                    message={t(`feedback.business.edit.${feedback}`)}
                    horizontal="right"
                    handleClose={() => setFeedback(null)}
                />
            )}
            <BusinessDetailsForm
                bodyRequest={bodyRequest}
                handleChange={handleChange}
                handleDataChange={handleDataChange}
                isEditing={isEditing}
                isNewBusiness={true}
                prevalentBusiness={primaryBusiness}
                usedAtecos={businessRes?.usedAtecos || []}
                loading={loadingGet || loadingPost || loadingPatch || loadingPatchAccounting}
                requestType={requestType}
                onSave={() => {
                    setRequestType("CREATE_OR_UPDATE");

                    //TODO: Add call to BFF instead of multiple calls on FE

                    if (businessId === "create") {
                        postBusiness({
                            data: {
                                ...bodyRequest,
                                asset: {
                                    assetGroup: "",
                                    assetSpecies: ""
                                }
                            }
                        })
                            .then(res => {
                                handleNavigate(res?.data?.id);
                            })
                            .catch(() => setFeedback(Feedback.ERROR));
                    } else {
                        const bodyDiff: any = getBodyDiff(bodyRequest, business);

                        patchBusiness({ data: bodyRequest as BusinessPatchDto })
                            .then(res => {
                                if (
                                    Object?.keys(bodyDiff)?.includes("selfEmployed") &&
                                    business?.accountingConfiguration?.id
                                ) {
                                    let simplifiedAccountingType: any =
                                        business?.accountingConfiguration?.simplifiedAccountingType;

                                    let vatReportsType = business?.accountingConfiguration?.vatReportsType;

                                    if (business?.accountingConfiguration?.accountingType === "ORDINARY") {
                                        simplifiedAccountingType = bodyDiff?.selfEmployed
                                            ? "CASH_ACCOUNTING"
                                            : "PAYMENTS_PRESUMPTION";
                                    } else if (business?.accountingConfiguration?.accountingType === "SIMPLIFIED") {
                                        vatReportsType = bodyDiff.selfEmployed
                                            ? "VAT_REPORTS_WITH_CASH_ANNOTATION"
                                            : vatReportsType;
                                        simplifiedAccountingType = bodyDiff?.selfEmployed
                                            ? "CASH_ACCOUNTING"
                                            : business?.accountingConfiguration?.simplifiedAccountingType;
                                    } else if (business?.accountingConfiguration?.accountingType === "FLAT_RATE") {
                                        simplifiedAccountingType = "CASH_ACCOUNTING";
                                    }

                                    patchBusinessAccounting({
                                        data: {
                                            accountingType: business?.accountingConfiguration?.accountingType,
                                            simplifiedAccountingType,
                                            vatReportsType
                                        }
                                    })
                                        .then(() => handleNavigate(res?.data?.id))
                                        .catch(() => setFeedback(Feedback.ERROR));
                                } else {
                                    handleNavigate(res?.data?.id);
                                }
                            })
                            .catch(() => setFeedback(Feedback.ERROR));
                    }
                }}
                onEditPrevalent={async () => {
                    setRequestType("EDIT_PREVALENT");
                    await patchBusiness({
                        url: `rnd:core:workspace-write:api://v1/workspaces/${workspaceId}/business/${primaryBusiness?.id}`,
                        data: { type: "SECONDARY" }
                    }).then(() => {
                        getBusinessList?.();
                        setBodyRequest(prev => ({
                            ...prev,
                            type: "MAIN"
                        }));
                    });
                }}
                onCancel={() => navigate(`/details/${workspaceId}/business`)}
                workspaceData={workspaceData}
            />
        </>
    );
};
